import { agentSchedulePolicies } from './schedules';
export const agentCatalogDefinitions = [
    {
        id: 'competitor_watch',
        name: 'Competitor Watch Agent',
        description: 'Tracks Sperant documentation and publishes evidence-backed competitor deltas.',
        ownerRuntime: 'queue',
        executionMode: 'queue',
        deliveryPhase: 'phase_1',
        defaultLane: 'competitor_alerts',
        artifactContracts: ['alert', 'battlecard', 'finding', 'proof'],
        responsibilities: [
            'Fetch official Sperant sources on a recurring cadence.',
            'Create evidence-backed competitor observations.',
            'Escalate material product or messaging changes.',
        ],
        queueMessageType: 'agent.market.competitor_watch',
        requiresHumanApproval: false,
        scheduleLabel: 'Daily watch + manual urgent reruns',
        schedulePolicy: agentSchedulePolicies.competitor_watch,
        enabledByDefault: true,
    },
    {
        id: 'proof_agent',
        name: 'Proof Agent',
        description: 'Validates direct-attack public claims against repo proof and cited market evidence.',
        ownerRuntime: 'queue',
        executionMode: 'queue',
        deliveryPhase: 'phase_1',
        defaultLane: 'evidence_gaps',
        artifactContracts: ['finding', 'proof', 'approval_request'],
        responsibilities: [
            'Fail closed when a direct public claim lacks proof.',
            'Flag expired or low-confidence evidence.',
            'Keep the public comparison surface evidence-backed.',
        ],
        queueMessageType: 'agent.market.proof_gate',
        requiresHumanApproval: true,
        scheduleLabel: 'Daily proof gate + pre-publish reruns',
        schedulePolicy: agentSchedulePolicies.proof_agent,
        enabledByDefault: true,
    },
    {
        id: 'search_demand',
        name: 'Search Demand Agent',
        description: 'Checks SEO-critical public pages and creates a backlog of missing demand capture opportunities.',
        ownerRuntime: 'queue',
        executionMode: 'queue',
        deliveryPhase: 'phase_1',
        defaultLane: 'seo_backlog',
        artifactContracts: ['finding', 'brief', 'experiment'],
        responsibilities: [
            'Verify sitemap, robots, canonical, and structured data coverage.',
            'Flag missing commercial landing page opportunities.',
            'Keep the Peru demand capture surface healthy.',
        ],
        queueMessageType: 'agent.marketing.search_demand',
        requiresHumanApproval: false,
        scheduleLabel: 'Daily',
        schedulePolicy: agentSchedulePolicies.search_demand,
        enabledByDefault: true,
    },
    {
        id: 'frontpage_cro',
        name: 'Frontpage CRO Agent',
        description: 'Audits direct-response surfaces on the deployed frontpage and flags conversion friction.',
        ownerRuntime: 'queue',
        executionMode: 'queue',
        deliveryPhase: 'phase_1',
        defaultLane: 'seo_backlog',
        artifactContracts: ['finding', 'experiment', 'brief'],
        responsibilities: [
            'Check demo-request presence on key pages.',
            'Check direct-attack pages for migration CTA coverage.',
            'Surface CRO regressions before they compound.',
        ],
        queueMessageType: 'agent.marketing.frontpage_cro',
        requiresHumanApproval: false,
        scheduleLabel: 'Daily',
        schedulePolicy: agentSchedulePolicies.frontpage_cro,
        enabledByDefault: true,
    },
    {
        id: 'abm_prospecting',
        name: 'ABM Prospecting Agent',
        description: 'Organizes named target accounts, briefs, and outreach prep for Peruvian developers.',
        ownerRuntime: 'backend',
        executionMode: 'manual',
        deliveryPhase: 'phase_1',
        defaultLane: 'abm_accounts',
        artifactContracts: ['brief', 'finding'],
        responsibilities: [
            'Maintain target-account watchlists.',
            'Generate research briefs from stored evidence.',
            'Prepare human-reviewed outbound motion.',
        ],
        requiresHumanApproval: true,
        schedulePolicy: agentSchedulePolicies.abm_prospecting,
        enabledByDefault: true,
    },
    {
        id: 'sales_coach',
        name: 'Sales Coach Agent',
        description: 'Prepares deal-specific discovery, objection handling, and follow-up guidance.',
        ownerRuntime: 'ai',
        executionMode: 'manual',
        deliveryPhase: 'phase_1',
        defaultLane: 'abm_accounts',
        artifactContracts: ['brief', 'battlecard'],
        responsibilities: [
            'Draft discovery plans and objection handling.',
            'Turn findings into sales-ready narratives.',
            'Keep humans in approval for outbound usage.',
        ],
        requiresHumanApproval: true,
        schedulePolicy: agentSchedulePolicies.sales_coach,
        enabledByDefault: true,
    },
    {
        id: 'deal_desk_pricing',
        name: 'Deal Desk / Pricing Agent',
        description: 'Creates auditable targeted pricing recommendations from approved playbooks.',
        ownerRuntime: 'backend',
        executionMode: 'manual',
        deliveryPhase: 'phase_1',
        defaultLane: 'pricing_desk',
        artifactContracts: ['pricing_recommendation', 'approval_request', 'brief'],
        responsibilities: [
            'Apply pricing playbooks consistently.',
            'Compute floor, target, recommended, and walk-away prices.',
            'Route exceptions through explicit approval.',
        ],
        requiresHumanApproval: true,
        enabledByDefault: true,
    },
    {
        id: 'migration_concierge',
        name: 'Migration Concierge Agent',
        description: 'Turns Sperant export readiness into a sales wedge backed by zero-loss import tooling.',
        ownerRuntime: 'sperant-translator',
        executionMode: 'manual',
        deliveryPhase: 'phase_1',
        defaultLane: 'abm_accounts',
        artifactContracts: ['brief', 'proof', 'finding'],
        responsibilities: [
            'Frame migration readiness from real Sperant exports.',
            'Produce zero-loss evidence and risk lists.',
            'Convert migration risk into a competitive advantage.',
        ],
        requiresHumanApproval: true,
        schedulePolicy: agentSchedulePolicies.migration_concierge,
        enabledByDefault: true,
    },
    {
        id: 'paid_media',
        name: 'Paid Media Agent',
        description: 'Optimizes paid acquisition once ad-platform and audience feedback loops are mature.',
        ownerRuntime: 'backend',
        executionMode: 'manual',
        deliveryPhase: 'later',
        defaultLane: 'seo_backlog',
        artifactContracts: ['experiment', 'finding', 'brief'],
        responsibilities: ['Later-phase agent placeholder in the roster.'],
        requiresHumanApproval: true,
        enabledByDefault: false,
    },
    {
        id: 'win_loss',
        name: 'Win/Loss Agent',
        description: 'Transforms won/lost deal evidence into product, marketing, and sales priorities.',
        ownerRuntime: 'backend',
        executionMode: 'manual',
        deliveryPhase: 'later',
        defaultLane: 'competitor_alerts',
        artifactContracts: ['battlecard', 'brief', 'finding'],
        responsibilities: ['Later-phase agent placeholder in the roster.'],
        requiresHumanApproval: true,
        enabledByDefault: false,
    },
    {
        id: 'release_truth',
        name: 'Release Truth Agent',
        description: 'Detects drift between marketed claims, shipped product, and release readiness.',
        ownerRuntime: 'backend',
        executionMode: 'manual',
        deliveryPhase: 'later',
        defaultLane: 'evidence_gaps',
        artifactContracts: ['finding', 'proof'],
        responsibilities: ['Later-phase agent placeholder in the roster.'],
        requiresHumanApproval: true,
        enabledByDefault: false,
    },
    {
        id: 'prod_reliability',
        name: 'Prod Reliability Agent',
        description: 'Monitors queue health, cron execution, and production signal regressions.',
        ownerRuntime: 'tail-worker',
        executionMode: 'manual',
        deliveryPhase: 'later',
        defaultLane: 'prod_issues',
        artifactContracts: ['alert', 'finding', 'brief'],
        responsibilities: ['Later-phase agent placeholder in the roster.'],
        requiresHumanApproval: false,
        enabledByDefault: false,
    },
    {
        id: 'data_integrity',
        name: 'Data Integrity Agent',
        description: 'Audits attribution, pricing, billing, and intake integrity across the operating system.',
        ownerRuntime: 'backend',
        executionMode: 'manual',
        deliveryPhase: 'later',
        defaultLane: 'prod_issues',
        artifactContracts: ['finding', 'proof', 'brief'],
        responsibilities: ['Later-phase agent placeholder in the roster.'],
        requiresHumanApproval: false,
        enabledByDefault: false,
    },
    {
        id: 'maintenance',
        name: 'Maintenance Agent',
        description: 'Tracks dependency drift, stale secrets, cron health, and operational hygiene.',
        ownerRuntime: 'backend',
        executionMode: 'manual',
        deliveryPhase: 'later',
        defaultLane: 'prod_issues',
        artifactContracts: ['finding', 'brief', 'alert'],
        responsibilities: ['Later-phase agent placeholder in the roster.'],
        requiresHumanApproval: false,
        enabledByDefault: false,
    },
];
